import { Link } from "react-router-dom";
import { ArrowRight, Radar, Video, KeyRound, Cpu, FileText, Calculator, Bot, Webhook, Check, ShieldCheck, Activity } from "lucide-react";
import Logo from "@/components/common/Logo";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import GridBackground from "@/components/common/GridBackground";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1734359052721-be5fddda39db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjBncmlkJTIwYmx1ZSUyMGRhcmt8ZW58MHx8fHwxNzc3NTA5NTYwfDA&ixlib=rb-4.1.0&q=85";

const PRESENCE = [
  { city: "Santiago", region: "Chile · LATAM", tag: "HQ" },
  { city: "Montreal", region: "Canada · North", tag: "OPS" },
  { city: "Florida", region: "USA · Caribbean gate", tag: "LOGISTICS" },
  { city: "Port-au-Prince", region: "Haiti · Island ops", tag: "FIELD" },
];

const PARTNERS = ["KANTECH", "AVIGILON", "LENEL ONGUARD", "MAGOS RADAR"];

export default function LandingPage() {
  const { t } = useLanguage();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      {/* Top Nav */}
      <nav className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="landing-logo-link"><Logo size={32} /></Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {user && typeof user === "object" ? (
            <Link
              to="/dashboard" data-testid="landing-dashboard-link"
              className="hidden sm:inline-flex items-center gap-2 px-4 h-9 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors"
            >
              {t("landing.cta.primary")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <>
              <Link
                to="/login" data-testid="landing-login-link"
                className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors link-underline"
              >{t("nav.login")}</Link>
              <Link
                to="/register" data-testid="landing-signup-link"
                className="inline-flex items-center gap-2 px-4 h-9 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors"
              >{t("nav.signup")}</Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <GridBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 text-[10px] font-mono tracking-[0.22em] text-primary uppercase mb-8" data-testid="landing-hero-badge">
              <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-primary" />
              {t("landing.badge")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-white" data-testid="landing-hero-title">
              {t("landing.hero.title")}
            </h1>
            <p className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed text-secondary-foreground/70">
              {t("landing.hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to={user && typeof user === "object" ? "/dashboard" : "/register"}
                data-testid="landing-primary-cta"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors glow-blue"
              >
                {t("landing.cta.primary")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login" data-testid="landing-secondary-cta"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-sm border border-border hover:border-primary text-secondary-foreground hover:text-primary transition-colors"
              >
                {t("landing.cta.secondary")}
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-[11px] font-mono tracking-widest text-muted-foreground uppercase">
              {PARTNERS.map((p) => (
                <div key={p} className="border-l border-border pl-4 first:border-l-0 first:pl-0">{p}</div>
              ))}
            </div>
          </div>

          {/* Right — radar illustration */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square lg:aspect-auto lg:h-full rounded-sm overflow-hidden border border-border bg-card">
              <img src={HERO_IMG} alt="Technical grid" className="absolute inset-0 w-full h-full object-cover opacity-45" />
              <div className="absolute inset-0 bg-gradient-to-br from-background/30 via-transparent to-background/70" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-64 h-64">
                  <div className="absolute inset-0 rounded-full border border-primary/25" />
                  <div className="absolute inset-8 rounded-full border border-primary/25" />
                  <div className="absolute inset-16 rounded-full border border-primary/25" />
                  <div className="absolute inset-24 rounded-full border border-primary/25" />
                  <div className="radar-sweep absolute inset-0 rounded-full" />
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-primary rounded-full shadow-[0_0_14px_rgba(0,168,255,0.9)]" />
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
                <span>SYS · OPERATIONAL</span>
                <span>LAT −33.44 · LON −70.66</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="relative border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="text-overline mb-4" data-testid="landing-services-kicker">01 · {t("landing.services.kicker")}</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">{t("landing.services.title")}</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            <ServiceCard Icon={KeyRound} title={t("landing.service.ac")}    desc={t("landing.service.ac.desc")}    tag="KANTECH · LENEL" test="service-ac" />
            <ServiceCard Icon={Video}    title={t("landing.service.vms")}   desc={t("landing.service.vms.desc")}   tag="AVIGILON ACC"   test="service-vms" />
            <ServiceCard Icon={Radar}    title={t("landing.service.radar")} desc={t("landing.service.radar.desc")} tag="MAGOS SR"       test="service-radar" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="text-overline mb-4">02 · {t("landing.features.kicker")}</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">
            From project intake to commissioning signoff — one operational canopy.
          </h2>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden">
            <FeatureCell Icon={Activity}    title={t("landing.feature1.title")} desc={t("landing.feature1.desc")} />
            <FeatureCell Icon={Bot}         title={t("landing.feature2.title")} desc={t("landing.feature2.desc")} />
            <FeatureCell Icon={Cpu}         title={t("landing.feature3.title")} desc={t("landing.feature3.desc")} />
            <FeatureCell Icon={FileText}    title={t("landing.feature4.title")} desc={t("landing.feature4.desc")} />
            <FeatureCell Icon={Calculator}  title={t("landing.feature5.title")} desc={t("landing.feature5.desc")} />
            <FeatureCell Icon={Webhook}     title={t("landing.feature6.title")} desc={t("landing.feature6.desc")} />
          </div>
        </div>
      </section>

      {/* Presence */}
      <section className="relative border-b border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="text-overline mb-4">03 · {t("landing.presence.kicker")}</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">{t("landing.presence.title")}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRESENCE.map((p) => (
              <div key={p.city} className="p-6 border border-border rounded-sm bg-background/40 relative overflow-hidden hover:border-primary/50 transition-colors" data-testid={`presence-${p.city.toLowerCase()}`}>
                <div className="absolute top-4 right-4 text-[9px] font-mono tracking-[0.24em] text-primary">{p.tag}</div>
                <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/25 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                </div>
                <div className="mt-6 text-xl text-white">{p.city}</div>
                <div className="mt-1 text-xs font-mono tracking-widest text-muted-foreground uppercase">{p.region}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative overflow-hidden">
        <GridBackground />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <h3 className="text-3xl lg:text-4xl font-semibold text-white tracking-tight">{t("landing.cta.primary")}</h3>
          <div className="mt-8 inline-flex items-center gap-3">
            <Link to="/register" data-testid="landing-footer-cta" className="inline-flex items-center gap-2 h-11 px-6 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors glow-blue">
              {t("nav.signup")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
            <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" /> Zero-footprint white-label</span>
            <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" /> 5 languages</span>
            <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" /> SOC-ready ops</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-4 text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
          <div>© {new Date().getFullYear()} · {t("landing.footer")}</div>
          <div>v1.0 · ACCESS · VMS · RADAR</div>
        </div>
      </footer>
    </div>
  );
}

function ServiceCard({ Icon, title, desc, tag, test }) {
  return (
    <div className="p-8 border border-border rounded-sm bg-card hover:border-primary/50 transition-colors group relative overflow-hidden" data-testid={test}>
      <div className="absolute -right-10 -top-10 w-32 h-32 bg-primary/5 rounded-full group-hover:bg-primary/10 transition-colors" />
      <div className="relative">
        <div className="w-11 h-11 rounded-sm bg-primary/10 border border-primary/25 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
        </div>
        <div className="mt-6 text-[10px] font-mono tracking-[0.24em] text-primary uppercase">{tag}</div>
        <h3 className="mt-2 text-xl text-white">{title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-secondary-foreground/70">{desc}</p>
      </div>
    </div>
  );
}

function FeatureCell({ Icon, title, desc }) {
  return (
    <div className="p-8 bg-background hover:bg-card transition-colors">
      <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
      <h4 className="mt-5 text-base text-white">{title}</h4>
      <p className="mt-2 text-sm leading-relaxed text-secondary-foreground/70">{desc}</p>
    </div>
  );
}
